import { reactive } from 'vue';

/**
 * Connection status states for tracking server connectivity.
 */
export enum ConnectionStatus {
  /** Actively receiving messages from server */
  CONNECTED = 'CONNECTED',
  /** Socket closed or no messages received recently, attempting reconnect */
  RECONNECTING = 'RECONNECTING',
  /** Initial state before first connection */
  DISCONNECTED = 'DISCONNECTED',
  /** Server is paused by admin - still connected but not processing ticks */
  SERVER_PAUSED = 'SERVER_PAUSED',
}

/**
 * Reactive state object for Vue integration.
 */
export interface ConnectionState {
  status: ConnectionStatus;
  lastMessageTime: number;
}

/**
 * Service to track connection health with the game server.
 *
 * Uses message timestamps to detect silent disconnects (server gone without
 * proper socket close). The state is reactive for Vue integration.
 */
export class ConnectionStateService {
  /** Timeout threshold (ms) - if no messages for this long, assume disconnected */
  private static readonly TIMEOUT_MS = 1500;

  /** Reactive state for Vue binding */
  public state: ConnectionState;

  constructor() {
    // Create reactive state so Vue components can track changes
    this.state = reactive({
      status: ConnectionStatus.DISCONNECTED,
      lastMessageTime: 0,
    });
  }

  /**
   * Check if currently connected to the server.
   * Returns true for both CONNECTED and SERVER_PAUSED (still connected, just paused).
   */
  public get isConnected(): boolean {
    return (
      this.state.status === ConnectionStatus.CONNECTED ||
      this.state.status === ConnectionStatus.SERVER_PAUSED
    );
  }

  /**
   * Check if the server is paused by an admin.
   */
  public get isPausedByServer(): boolean {
    return this.state.status === ConnectionStatus.SERVER_PAUSED;
  }

  /**
   * Called when any message is received from the server.
   * Updates timestamp and transitions to CONNECTED if disconnected/reconnecting.
   * Does NOT override SERVER_PAUSED - that's controlled by serverPaused messages.
   */
  public onMessageReceived(): void {
    this.state.lastMessageTime = performance.now();
    if (
      this.state.status === ConnectionStatus.DISCONNECTED ||
      this.state.status === ConnectionStatus.RECONNECTING
    ) {
      this.state.status = ConnectionStatus.CONNECTED;
    }
  }

  /**
   * Called when server sends a pause state change.
   */
  public onServerPaused(isPaused: boolean): void {
    if (isPaused) {
      this.state.status = ConnectionStatus.SERVER_PAUSED;
    } else {
      this.state.status = ConnectionStatus.CONNECTED;
    }
    // Update timestamp so we don't trigger timeout
    this.state.lastMessageTime = performance.now();
  }

  /**
   * Called when WebSocket connection opens.
   */
  public onSocketOpen(): void {
    this.state.status = ConnectionStatus.CONNECTED;
    this.state.lastMessageTime = performance.now();
  }

  /**
   * Called when WebSocket connection closes.
   */
  public onSocketClose(): void {
    this.state.status = ConnectionStatus.RECONNECTING;
  }

  /**
   * Check for silent disconnect (no messages received within timeout).
   * Should be called each frame. Skipped when server is paused.
   */
  public checkTimeout(): void {
    // Don't timeout when server is paused - no messages expected
    if (this.state.status === ConnectionStatus.CONNECTED) {
      const elapsed = performance.now() - this.state.lastMessageTime;
      if (elapsed > ConnectionStateService.TIMEOUT_MS) {
        this.state.status = ConnectionStatus.RECONNECTING;
      }
    }
  }

  /**
   * Reset to initial disconnected state (for testing).
   */
  public reset(): void {
    this.state.status = ConnectionStatus.DISCONNECTED;
    this.state.lastMessageTime = 0;
  }
}
